import { useEffect, useState } from 'react';
import { Button, Card, Icon, Table, cn } from '@evogard/ui';
import { subtract, type Money } from '@evogard/domain';
import type { FinanceDashboardRowWire } from '@evogard/contract';
import { compact, money } from './money.js';
import { RankingSkeleton, columns, tooltipOf } from './RankingBars.js';

/**
 * Um corte do painel como ranking do SALDO (29/10/2026, para a direção, no
 * corte por time): uma pergunta só — qual linha dá mais resultado. A barra
 * sai do meio, verde para a direita quando a receita passa a despesa e
 * vermelha para a esquerda quando não passa, com a receita e a despesa
 * escritas embaixo.
 *
 * As duas metades usam a mesma escala (o maior saldo, de qualquer sinal),
 * senão o comprimento mentiria. A lacuna — sem time, sem consultor — vai no
 * fim, em itálico, fora da ordem.
 */
export function BalanceRanking({
  title,
  descricao,
  rows,
  first,
  loading,
}: {
  title: string;
  descricao: string;
  rows: readonly FinanceDashboardRowWire[] | undefined;
  /** O nome da primeira coluna da tabela: Time. */
  first: string;
  loading: boolean;
}) {
  const [asTable, setAsTable] = useState(false);
  const list = rows ?? [];

  return (
    <Card
      title={title}
      descricao={descricao}
      actions={
        list.length > 0 && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        )
      }
      bleed={asTable}
    >
      {loading && list.length === 0 ? (
        <RankingSkeleton label={title} />
      ) : list.length === 0 ? (
        <p className="py-8 text-center text-sm text-texto-suave">
          Nenhum lançamento no período.
        </p>
      ) : asTable ? (
        <Table<FinanceDashboardRowWire>
          label={`Receita e despesa ${title.toLowerCase()}`}
          empty="Nenhum lançamento no período."
          items={[...list]}
          rowKey={(r) => r.key}
          columns={columns(first)}
        />
      ) : (
        <Bars rows={list} />
      )}
    </Card>
  );
}

const balanceOf = (r: FinanceDashboardRowWire): Money =>
  subtract(money(r.revenue.amount), money(r.expense.amount));

function Bars({ rows }: { rows: readonly FinanceDashboardRowWire[] }) {
  // nasce em zero e cresce no quadro seguinte, para a transição ter de onde partir
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const ordered = [
    ...rows.filter((r) => !r.gap).sort((a, b) => balanceOf(b) - balanceOf(a)),
    ...rows.filter((r) => r.gap),
  ];
  const max = Math.max(1, ...ordered.map((r) => Math.abs(balanceOf(r))));

  return (
    <ul className="flex flex-col gap-1">
      {ordered.map((r, i) => {
        const balance = balanceOf(r);
        const half = (Math.abs(balance) / max) * 50;
        return (
          <li
            key={r.key}
            title={tooltipOf(r)}
            className="grid grid-cols-[minmax(0,10rem)_1fr_6rem] items-center gap-x-3 rounded-borda px-1 py-1.5 hover:bg-superficie-suave"
          >
            <span
              className={cn(
                'truncate text-sm',
                r.gap ? 'italic text-texto-suave' : 'font-semibold text-texto',
              )}
            >
              {r.label}
            </span>
            <span className="relative h-4">
              <span className="absolute inset-y-[-4px] left-1/2 border-l border-dashed border-borda-forte" />
              {balance !== 0 && (
                <span
                  className={cn(
                    'absolute top-0.5 h-3 rounded-sm transition-[width] duration-700 ease-out',
                    balance > 0 ? 'left-1/2 bg-sucesso' : 'right-1/2 bg-erro',
                  )}
                  style={{
                    width: grown ? `${Math.max(0.75, half)}%` : '0%',
                    transitionDelay: `${i * 70}ms`,
                  }}
                />
              )}
            </span>
            <span
              className={cn(
                'text-right text-sm font-semibold tabular-nums',
                balance > 0
                  ? 'text-sucesso'
                  : balance < 0
                    ? 'text-erro'
                    : 'text-texto-suave',
              )}
            >
              {balance > 0 ? '+' : ''}
              {compact(balance)}
            </span>
            <span className="col-start-2 col-end-4 text-xs text-texto-suave tabular-nums">
              {compact(money(r.revenue.amount))} de receita ·{' '}
              {compact(money(r.expense.amount))} de despesa
            </span>
          </li>
        );
      })}
    </ul>
  );
}
